import { NgIf, CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Store } from '@ngrx/store';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { DataUsuario } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { AppState } from 'app/store/app.reducers';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [MatTooltipModule, CommonModule, FuseLoadingBarComponent, FuseVerticalNavigationComponent, NotificationsComponent, UserComponent, NgIf, MatIconModule, MatButtonModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, RouterOutlet, QuickChatComponent],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    private subscriptionStore: Subscription = new Subscription();
    isScreenSmall: boolean;
    navigation: Navigation;
    user: DataUsuario;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    pharmacyName: string = '';
    pharmacyCuit: string = '';
    pharmacyAddress: string = '';
    userFullName: string = '';
    userEmail: string = '';

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private store: Store<AppState>,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: DataUsuario) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this.subscriptionStore = this.store.select((state) => ({
            configuracion: state.configuracion,
            datosInicio: state.datosInicio,
        }))
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ configuracion, datosInicio }) => {
                const usuarioActual = configuracion.usuarioActual;
                const usuarioNombre = this.getTextValue(usuarioActual?.nombre);
                const usuarioApellido = this.getTextValue(usuarioActual?.apellido);
                this.userFullName = [usuarioNombre, usuarioApellido].filter(Boolean).join(' ');
                this.userEmail = this.getTextValue(usuarioActual?.email);

                const prestadorRequest = datosInicio.PrestadorRequest;
                const prestadorInicial = configuracion.datosIniciales?.prestadores?.[0];
                const primerLugarAtencion = prestadorInicial?.lugares_atencion?.[0];

                this.pharmacyName = this.getTextValue(prestadorRequest?.RazonSocial)
                    || this.getTextValue(prestadorInicial?.razon_social)
                    || this.getTextValue(primerLugarAtencion?.referencia);
                this.pharmacyCuit = this.getTextValue(prestadorRequest?.CuitPrestador)
                    || this.getTextValue(prestadorInicial?.cuit);
                this.pharmacyAddress = this.getTextValue(prestadorRequest?.Direccion)
                    || this.getTextValue(primerLugarAtencion?.domicilio);
            })
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    private getTextValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        const text = String(value).trim();
        const lowerText = text.toLowerCase();

        if (!text || lowerText === 'undefined' || lowerText === 'null') {
            return '';
        }

        return text;
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
